import {ChangeDetectorRef, Component} from "@angular/core";
import {AnalyticsService} from "../../../orchestration/analytics.service";
import {SessionModel} from "../../../orchestration/session.model";
import {NavigationService} from "../../../orchestration/navigation.service";
import {Page, PageGeometry} from "../../../orchestration/page";
import {MenuModel} from "../../../menu/menu.model";
import {DealModel} from "../../../deal/deal.model";
import {OrderModel} from "../../order.model";
import {OrderingContextModel} from "../../../ordering-context/ordering-context.model";
import {MessageLevel} from "../../../notification/message/message";
import {StringUtils} from "../../../string-utils/string-utils";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {ResponsiveService} from "../../../orchestration/responsive.service";
import {NavigationParams} from "../../../orchestration/navigation-params";
import {OrderItem} from "../../order";
import {MenuCategoryData, MenuExtraGroupData, MenuItemData} from "../../../menu/menu-entity.model";

class PromotionResult {
    constructor(public changesMade: boolean, public proceed: boolean) {
    }
}

@Component({
    selector: "woa-order-item-edit-page",
    templateUrl: "./order-item-edit-page.component.html",
    providers: [{provide: Page, useExisting: OrderItemEditPageComponent}]
})
export class OrderItemEditPageComponent extends Page {
    MessageLevel = MessageLevel;
    StringUtils = StringUtils;

    public category: MenuCategoryData;
    public menuItem: MenuItemData;
    public orderItem: OrderItem;
    public singleItem: boolean = false;
    public originalOrderItem: OrderItem;
    public itemsToOrder: OrderItem[];

    private readonly editing: boolean = false;
    private readonly isSimpleProduct: boolean;
    private readonly allAreSimpleProducts: boolean;
    private readonly containsDetails: boolean;
    private readonly anySummaryContainsDetails: boolean;

    private itemsOrdered: boolean = false;
    private orderItemIndex: number = 0;
    private extraGroupInteracted: MenuExtraGroupData[] = [];
    private _mobileHeaderStuck: boolean = false;

    // Used to destroy the view, so it will scroll to the top when going to the next item
    contentVisible: boolean = true;

    constructor(navigationParams: NavigationParams,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public menuModel: MenuModel,
                public dealModel: DealModel,
                public responsiveService: ResponsiveService,
                private navigationService: NavigationService,
                private changeDetectorRef: ChangeDetectorRef,
                private orderModel: OrderModel,
                private analyticsService: AnalyticsService) {
        super();

        this.category = navigationParams.get("category");
        if (navigationParams.get("orderItem")) {
            this.originalOrderItem = navigationParams.get("orderItem");
            this.orderItem = this.originalOrderItem.createClone();
            this.menuItem = this.orderItem.menuItem;
            this.editing = true;
            this.singleItem = true;
        } else if (navigationParams.get("menuItem")) {
            this.menuItem = navigationParams.get("menuItem");
            this.orderItem = this.orderModel.createOrderItem(this.menuItem, this.menuItem.sellable.minWebOrderQty || 1);
            this.singleItem = true;
        } else {
            this.itemsToOrder = navigationParams.get("itemsToOrder");
            this.orderItem = this.itemsToOrder[0];
            this.menuItem = this.orderItem.menuItem;
        }

        this.orderModel.addToOrderAttempted = false;

        if (this.singleItem) {
            this.isSimpleProduct = menuModel.isSimpleProductItem(this.menuItem);
            this.containsDetails = menuModel.itemSummaryContainsDetails(this.menuItem);
        } else {
            this.allAreSimpleProducts = this.itemsToOrder.every((orderItem: OrderItem) => menuModel.isSimpleProductItem(orderItem.menuItem, true));
            this.anySummaryContainsDetails = this.itemsToOrder.some((orderItem: OrderItem) => menuModel.itemSummaryContainsDetails(orderItem.menuItem, true));
        }
    }

    get productImage(): FedeltaImageData {
        return this.menuModel.getItemImage(undefined, this.menuItem, false);
    }

    get title(): string {
        return this.singleItem ? "woa.heading.order.item-edit.itemTitle"
            : this.itemsToOrder.length > 1 ? "woa.heading.order.item-edit.dealTitleWithProgress"
                : "woa.heading.order.item-edit.dealTitle";
    }

    get subtitle(): string {
        return this.itemsToOrder ? "woa.heading.order.item-edit.dealSubtitle" : undefined;
    }

    get titleParams(): any {
        return {
            "itemName": this.menuItem.name,
            "dealName": this.dealModel.getDealName(this.dealModel.pendingDeal),
            "itemIndex": this.orderItemIndex + 1,
            "nrItems": this.itemsToOrder && this.itemsToOrder.length,
            "dealMessage": this.dealModel.pendingDeal && this.dealModel.pendingDeal.description
        };
    }

    get isLastOrderItem(): boolean {
        return this.orderItemIndex === this.itemsToOrder.length - 1;
    }

    get addItemToOrderLabel(): string {
        return this.singleItem ? (this.editing ? "woa.button.order.item-edit.confirmAtPrice" : "woa.button.order.item-edit.addQtyForPrice")
            : this.isLastOrderItem ? (this.itemsToOrder.length > 1 ? "woa.button.order.item-edit.addDealItemsToOrder" : "woa.button.order.addToOrder")
                : "woa.button.order.item-edit.nextDealItem";
    }

    get hasSummaryContent(): boolean {
        return this.singleItem ? this.containsDetails : this.anySummaryContainsDetails;
    }

    get showDesktopOptionsPanel(): boolean {
        return this.singleItem ? !this.isSimpleProduct : !this.allAreSimpleProducts;
    }

    get desktopCustomGeometry(): PageGeometry {
        return {
            pillarboxedOnDesktop: "none",
            width: this.showDesktopOptionsPanel ? "90%" : "450px",
            height: this.showDesktopOptionsPanel ? "95%" : this.productImage && this.productImage.id && this.hasSummaryContent ? "660px" : "507px"
        };
    }

    get mobileHeaderStuck(): boolean {
        return this._mobileHeaderStuck;
    }

    set mobileHeaderStuck(mobileHeaderStuck: boolean) {
        this._mobileHeaderStuck = mobileHeaderStuck;
        this.changeDetectorRef.detectChanges();
    }

    get showItemWaitTimeInfo(): boolean {
        // Note: we display a wait time information notice if a standard wait time is set, and is not exceeded by the current wait time.
        return this.menuItem.sellable.standardWaitTimeMinutes && (this.menuItem.sellable.standardWaitTimeMinutes >= (this.menuItem.sellable.currentWaitTimeMinutes || 0)) && this.orderingContextModel.showWaitTimeNotices;
    }

    get showItemWaitTimeWarning(): boolean {
        // Note: we display a wait time warning notice if the current wait time exceeds the standard wait time.
        return (this.menuItem.sellable.currentWaitTimeMinutes || 0) > (this.menuItem.sellable.standardWaitTimeMinutes || 0) && this.orderingContextModel.showWaitTimeNotices;
    }

    onDidDismissPage(): void {
        if (!this.singleItem && !this.itemsOrdered) {
            this.dealModel.cancelPendingDeal();
        }
    }

    private hasExtraGroupInteracted(extraGroup: MenuExtraGroupData): boolean {
        return this.extraGroupInteracted.indexOf(extraGroup) >= 0;
    }

    public setExtraGroupInteracted(extraGroup: MenuExtraGroupData): void {
        if (!this.hasExtraGroupInteracted(extraGroup)) {
            this.extraGroupInteracted.push(extraGroup);
        }
    }

    sizeSelected(menuItem: MenuItemData): void {
        this.menuItem = menuItem;
        const newOrderItem: OrderItem = this.orderModel.createOrderItem(this.menuItem, this.orderItem.quantity, false);
        newOrderItem.assignExtras(this.orderItem, menuItem);

        // We don't add default extras when creating the order item above, because we want to assign the existing extras from the previously selected size first.
        // Defaults will then only be added for extra groups which were not on the previous size.
        newOrderItem.addDefaultExtras(undefined, undefined, menuItem.sellable.extras);

        this.orderItem = newOrderItem;

        while (this.orderItem.quantity > 1 && this.orderModel.orderItemAtLimit(this.orderItem, 1, this.orderItem.quantity - 1)) {
            this.orderItem.quantity--;
        }

        this.changeDetectorRef.detectChanges();
    }

    async addItemToOrder(): Promise<any> {
        if (!this.orderModel.validateOrderItem(this.orderItem)) {
            this.orderModel.addToOrderAttempted = true;
            return;
        }

        if (!this.editing) {
            let revalidate: boolean = false;
            let result: PromotionResult = new PromotionResult(false, true);
            if (this.orderItem.compositeOrderItems) {
                for (const component of this.orderItem.compositeOrderItems) {
                    result = await this.promptForPromotedExtras(<OrderItem>component, this.orderItem);
                    if (result.changesMade) {
                        revalidate = true;
                    }
                    if (!result.proceed) {
                        break;
                    }
                }
            } else {
                result = await this.promptForPromotedExtras(this.orderItem, this.orderItem);
                if (result.changesMade) {
                    revalidate = true;
                }
            }
            if (revalidate && !this.orderModel.validateOrderItem(this.orderItem)) {
                this.orderModel.addToOrderAttempted = true;
                return;
            }
            if (!result.proceed) {
                return;
            }
        }

        if (this.editing) {
            this.orderModel.replaceOrderItemByIdentifier(this.originalOrderItem.identifier, this.orderItem);
            this.itemsOrdered = true;
            return this.navigationService.dismissPage(this, this.orderItem);
        }

        if (this.singleItem) {
            this.orderModel.addOrderItem(this.orderItem);
            // noinspection ES6MissingAwait
            this.analyticsService.trackItemAddedToOrder(this.orderItem, this.menuItem ? this.menuItem.promotedFeatureTag : undefined);
            this.itemsOrdered = true;
            return this.navigationService.dismissPage(this, this.orderItem);
        }

        if (this.isLastOrderItem) {
            this.itemsToOrder.forEach((orderItem: OrderItem) => {
                this.orderModel.addOrderItem(orderItem);
                // noinspection JSIgnoredPromiseFromCall
                this.analyticsService.trackItemAddedToOrder(orderItem);
            });
            this.itemsOrdered = true;
            this.dealModel.applyDeal(this.dealModel.pendingDeal);
            return this.navigationService.dismissPage(this, this.orderItem);
        }

        this.orderItemIndex++;
        this.contentVisible = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
            this.orderItem = this.itemsToOrder[this.orderItemIndex];
            this.menuItem = this.orderItem.menuItem;
            this.contentVisible = true;
            this.changeDetectorRef.detectChanges();
        }, 100);
    }

    /**
     * Prompt for promoted multi-select extras where the user hasn't interacted with the extra group.
     * @param orderItem
     * @param mainOrderItem
     */
    async promptForPromotedExtras(orderItem: OrderItem, mainOrderItem: OrderItem): Promise<PromotionResult> {
        let anyChangesMade: boolean = false;
        for (const extraGroup of orderItem.availableExtraGroups) {
            if (this.menuModel.extraGroupIsSingleSelect(extraGroup) || !extraGroup.promoted || this.hasExtraGroupInteracted(extraGroup) || !orderItem.isExtraGroupConditionallyAvailable(extraGroup)) {
                continue;
            }

            const originalLocalTotal: number = orderItem.localTotal;
            // noinspection ES6MissingAwait
            this.analyticsService.trackExtraPromoShown(mainOrderItem.name, orderItem === mainOrderItem ? undefined : orderItem.name, extraGroup.name,
                mainOrderItem.sellable.plu, orderItem == mainOrderItem ? undefined : orderItem.sellable.plu);
            this.setExtraGroupInteracted(extraGroup);
            let editOrderItem: OrderItem = orderItem.createClone();
            const changesMode: boolean = await this.navigationService.createPage("woa-order-item-combined-extras-edit-page", {
                orderItem: editOrderItem,
                mainOrderItem: mainOrderItem,
                originalOrderItem: this.originalOrderItem,
                menuItem: this.menuItem,
                promotedExtraGroup: extraGroup
            });
            if (changesMode) {
                anyChangesMade = true;
                orderItem.assignExtras(editOrderItem, undefined, true);

                // noinspection ES6MissingAwait
                this.analyticsService.trackExtraPromoOrdered(mainOrderItem.name, orderItem === mainOrderItem ? undefined : orderItem.name, extraGroup.name,
                    mainOrderItem.sellable.plu, orderItem == mainOrderItem ? undefined : orderItem.sellable.plu, orderItem.localTotal - originalLocalTotal);
            } else {
                // noinspection ES6MissingAwait
                this.analyticsService.trackExtraPromoCancelled(mainOrderItem.name, orderItem === mainOrderItem ? undefined : orderItem.name, extraGroup.name,
                    mainOrderItem.sellable.plu, orderItem == mainOrderItem ? undefined : orderItem.sellable.plu);

                // Dismissing the page (as opposed to explicitly declining) stops further promos and returns to editing the product
                if (changesMode !== false) {
                    return new PromotionResult(anyChangesMade, false);
                }
            }
        }
        return new PromotionResult(anyChangesMade, true);
    }

    desktopContentStickyTop(content: HTMLElement): string {
        return this.pageLayout && this.pageLayout.scrollerElementRef ? Math.min(this.pageLayout.scrollerElementRef.nativeElement.clientHeight - content.offsetHeight, 0) + "px" : undefined;
    }
}



// WEBPACK FOOTER //
// ./src/app/order/order-item/order-item-edit-page/order-item-edit-page.component.ts